import React from 'react';
import { parse } from 'csv-parse/browser/esm/sync';
import { Button, Table, TableColumn } from 'common/components';
import { replaceSpamNumbers, SpamCheckResult, SpamReplacementResult } from 'services/api/PhoneNumberAPI';
import styles from '../PhoneNumberManager.module.scss';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_ROWS = 100000; // Limit number of rows to prevent memory issues

interface ReplaceSpamTabProps {
    showNotification: (message: string, type: 'success' | 'error') => void;
}

const ReplaceSpamTab: React.FC<ReplaceSpamTabProps> = ({ showNotification }) => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [csvFile, setCsvFile] = React.useState<File | null>(null);
    const [spamResults, setSpamResults] = React.useState<SpamReplacementResult[] | null>(null);

    // Define table columns for spam replacement results
    const resultColumns: TableColumn<SpamReplacementResult>[] = [
        {
            key: 'status',
            title: 'Status',
            render: (_, record) => (
                <span className={record.success ? styles.statusSuccess : styles.statusFailed}>
                    {record.success ? '✓ Success' : '✗ Failed'}
                </span>
            ),
            width: 120,
        },
        {
            key: 'oldNumber',
            title: 'Old Number',
            dataIndex: 'oldNumber',
        },
        {
            key: 'newNumber',
            title: 'New Number',
            render: (_, record) => record.newNumber || '-',
        },
        {
            key: 'tenantName',
            title: 'Tenant',
            dataIndex: 'tenantName',
        },
        {
            key: 'areaCode',
            title: 'Area Code',
            render: (_, record) => (record.areaCode ? String(record.areaCode) : '-'),
            width: 100,
        },
        {
            key: 'error',
            title: 'Error',
            render: (_, record) => record.releaseError || record.purchaseError || '-',
        },
    ];

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file size
            if (file.size > MAX_FILE_SIZE) {
                showNotification(
                    `File size exceeds maximum limit of ${MAX_FILE_SIZE / (1024 * 1024)} MB. Please upload a smaller file.`,
                    'error'
                );
                event.target.value = ''; // Clear the input
                return;
            }

            // Validate file type
            if (!file.name.toLowerCase().endsWith('.csv')) {
                showNotification('Please upload a valid CSV file.', 'error');
                event.target.value = ''; // Clear the input
                return;
            }

            setCsvFile(file);
            setSpamResults(null);
        }
    };

    const parseCsvFile = (file: File): Promise<SpamCheckResult[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target?.result as string;

                    // Parse CSV with csv-parse library
                    const records = parse(text, {
                        columns: true, // Use first row as header
                        skip_empty_lines: true,
                        trim: true,
                        relax_quotes: true,
                        relax_column_count: true,
                    }) as Record<string, any>[];

                    // Validate row count
                    if (records.length === 0) {
                        reject(new Error('CSV file must contain at least one data row'));
                        return;
                    }

                    if (records.length > MAX_ROWS) {
                        reject(
                            new Error(
                                `CSV file contains ${records.length} rows, which exceeds the maximum limit of ${MAX_ROWS} rows.`
                            )
                        );
                        return;
                    }

                    // Get normalized column names from first record
                    const firstRecord = records[0];
                    const columnMap: Record<string, string> = {};

                    Object.keys(firstRecord).forEach((key) => {
                        const normalizedKey = key.trim().toLowerCase();
                        columnMap[normalizedKey] = key;
                    });

                    // Find phone number column (flexible naming)
                    const phoneNumberKey = columnMap['phone number'] ?? columnMap['phone'] ?? columnMap['phonenumber'];

                    // Find display result column (flexible naming)
                    const displayResultKey =
                        columnMap['display result'] ??
                        columnMap['displayresult'] ??
                        columnMap['result'] ??
                        columnMap['status'];

                    if (!phoneNumberKey) {
                        reject(new Error('Could not find "Phone Number" column in CSV header'));
                        return;
                    }

                    if (!displayResultKey) {
                        reject(new Error('Could not find "Display Result" column in CSV header'));
                        return;
                    }

                    // Process records
                    const spamCheckResults: SpamCheckResult[] = records
                        .map((record: any) => {
                            const phoneNumber = record[phoneNumberKey]?.trim() || '';
                            const displayResult = record[displayResultKey]?.trim() || '';

                            return {
                                phoneNumber,
                                displayResult,
                            };
                        })
                        .filter((result) => {
                            // Only include rows with phone number and display result
                            if (!result.phoneNumber || !result.displayResult) {
                                return false;
                            }

                            // Check if display result indicates spam
                            const resultLower = result.displayResult.toLowerCase();
                            return (
                                resultLower.includes('spam') ||
                                resultLower.includes('scam') ||
                                resultLower.includes('fraud') ||
                                resultLower.includes('blocked')
                            );
                        });

                    resolve(spamCheckResults);
                } catch (error: any) {
                    reject(new Error(`Failed to parse CSV file: ${error.message || 'Unknown error'}`));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    };

    const downloadCsv = (content: string, filename: string) => {
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadOreoDelete = () => {
        if (!spamResults) return;

        const oldNumbers = spamResults.map((result) => result.oldNumber);
        const csvContent = oldNumbers.join('\n');

        downloadCsv(csvContent, 'oreo_delete.csv');
        showNotification('Downloaded oreo_delete.csv successfully', 'success');
    };

    const handleDownloadNewOreo = () => {
        if (!spamResults) return;

        const successfulReplacements = spamResults.filter((result) => result.success && result.newNumber);
        const csvRows = successfulReplacements.map((result) => `${result.newNumber},Multi-Use Line`);
        const csvContent = csvRows.join('\n');

        downloadCsv(csvContent, 'new_oreo.csv');
        showNotification('Downloaded new_oreo.csv successfully', 'success');
    };

    const handleSpamUpload = async () => {
        if (!csvFile) {
            showNotification('Please select a CSV file', 'error');
            return;
        }

        setIsSubmitting(true);
        setSpamResults(null);

        try {
            const spamCheckResults = await parseCsvFile(csvFile);

            if (spamCheckResults.length === 0) {
                showNotification('No valid spam numbers found in CSV', 'error');
                setIsSubmitting(false);
                return;
            }

            const response = await replaceSpamNumbers({ spamCheckResults });

            setSpamResults(response.results);
            showNotification(
                response.message ||
                    `Successfully processed ${response.totalSpamNumbers} spam numbers. Replaced: ${response.replaced}, Failed: ${response.failed}, Not Found: ${response.notFound}`,
                'success'
            );
        } catch (error: any) {
            console.error('Error replacing spam numbers:', error);
            const errorMessage =
                error.response?.data?.message || error.message || 'Failed to replace spam numbers. Please try again.';
            showNotification(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.spamContainer}>
            <div className={styles.formSection}>
                <h2 className={styles.sectionTitle}>Upload Spam Numbers CSV</h2>
                <p className={styles.description}>
                    Upload a CSV file with spam numbers to replace. The CSV must contain a header row with at least two
                    columns: "Phone Number" and "Display Result". Only rows where Display Result contains "spam",
                    "scam", "fraud", or "blocked" will be processed. Additional columns are optional.
                </p>
                <p className={styles.description}>
                    <strong>File Limits:</strong> Maximum file size: {MAX_FILE_SIZE / (1024 * 1024)} MB | Maximum rows:{' '}
                    {MAX_ROWS.toLocaleString()}
                </p>

                <div className={styles.uploadSection}>
                    <input
                        type='file'
                        accept='.csv'
                        onChange={handleFileChange}
                        className={styles.fileInput}
                        id='csv-upload'
                    />
                    <label htmlFor='csv-upload' className={styles.fileLabel}>
                        {csvFile ? csvFile.name : 'Choose CSV file'}
                    </label>

                    <Button variant='primary' disabled={!csvFile || isSubmitting} onClick={handleSpamUpload}>
                        {isSubmitting ? 'Processing...' : 'Replace Spam Numbers'}
                    </Button>
                </div>
            </div>

            {spamResults && spamResults.length > 0 && (
                <div className={styles.resultsSection}>
                    <div className={styles.resultsHeader}>
                        <h2 className={styles.sectionTitle}>Replacement Results</h2>
                        <div className={styles.downloadButtons}>
                            <Button variant='secondary' onClick={handleDownloadOreoDelete}>
                                Download oreo_delete.csv
                            </Button>
                            <Button variant='secondary' onClick={handleDownloadNewOreo}>
                                Download new_oreo.csv
                            </Button>
                        </div>
                    </div>
                    <Table
                        data={spamResults}
                        columns={resultColumns}
                        rowKey={(record, index) => `${record.oldNumber}-${index}`}
                        rowClassName={(record) => (record.success ? styles.successRow : styles.failureRow)}
                        bordered
                        striped
                        hoverable
                    />
                </div>
            )}
        </div>
    );
};

export default ReplaceSpamTab;
